const COMMON_VIDEO_FPS = [24, 25, 30, 48, 50, 60] as const

type VideoFrameCallbackMetadata = {
  mediaTime: number
  presentedFrames: number
}

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => void,
  ) => number
}

function median(values: number[]): number {
  if (values.length === 0) {
    return 30
  }
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2
  }
  return sorted[mid]
}

/** Snap to broadcast fps; prefer the lower candidate when between 30 and 48 to avoid over-pacing. */
export function snapToCommonVideoFps(fps: number): number {
  if (!Number.isFinite(fps) || fps <= 0) {
    return 30
  }

  const clamped = Math.max(1, Math.min(120, fps))
  let best = 30
  let bestDiff = Infinity
  for (const candidate of COMMON_VIDEO_FPS) {
    const diff = Math.abs(clamped - candidate)
    if (diff < bestDiff || (diff === bestDiff && candidate < best)) {
      bestDiff = diff
      best = candidate
    }
  }
  return best
}

/** Prefer a common fps not greater than measured — avoids encoder/capture duplicate frames. */
export function snapToCommonVideoFpsConservative(fps: number): number {
  if (!Number.isFinite(fps) || fps <= 0) {
    return 30
  }

  const snapped = snapToCommonVideoFps(fps)
  if (snapped <= fps + 0.5) {
    return snapped
  }

  const lowerCandidates = COMMON_VIDEO_FPS.filter((candidate) => candidate < snapped)
  if (lowerCandidates.length > 0) {
    return lowerCandidates[lowerCandidates.length - 1]
  }
  return snapped
}

export type EstimateVideoFrameRateOptions = {
  timeoutMs?: number
  sampleDurationMs?: number
  fallbackFps?: number
}

/**
 * Measure actual playback fps via presentedFrames over wall time (more reliable than per-frame deltas).
 */
export async function estimateVideoFrameRate(
  videoEl: HTMLVideoElement,
  options: EstimateVideoFrameRateOptions = {},
): Promise<number> {
  const timeoutMs = options.timeoutMs ?? 3000
  const sampleDurationMs = options.sampleDurationMs ?? 900
  const fallbackFps = options.fallbackFps ?? 30

  const video = videoEl as VideoWithFrameCallback
  if (!video.requestVideoFrameCallback) {
    return fallbackFps
  }

  return new Promise((resolve) => {
    let settled = false
    let startWallMs = 0
    let startPresented = 0
    const deltas: number[] = []
    let lastMediaTime = -1

    const finish = (rawFps: number) => {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(timer)
      resolve(snapToCommonVideoFpsConservative(rawFps))
    }

    const timer = setTimeout(() => {
      if (startWallMs > 0) {
        finish(fallbackFps)
        return
      }
      if (deltas.length > 0) {
        finish(median(deltas.map((delta) => 1 / delta)))
        return
      }
      finish(fallbackFps)
    }, timeoutMs)

    const schedule = () => {
      if (settled) {
        return
      }
      video.requestVideoFrameCallback!((now, metadata) => {
        if (lastMediaTime >= 0) {
          const delta = metadata.mediaTime - lastMediaTime
          if (delta > 0.001 && delta < 0.5) {
            deltas.push(delta)
          }
        }
        lastMediaTime = metadata.mediaTime

        if (startWallMs === 0) {
          startWallMs = now
          startPresented = metadata.presentedFrames
          schedule()
          return
        }

        const elapsedMs = now - startWallMs
        if (elapsedMs >= sampleDurationMs) {
          const frameCount = metadata.presentedFrames - startPresented
          if (frameCount > 0) {
            finish(frameCount / (elapsedMs / 1000))
            return
          }
        }

        schedule()
      })
    }

    schedule()
  })
}

export function resolveTrackFrameRate(track: MediaStreamTrack, fallback: number): number {
  const settings = track.getSettings?.()
  if (typeof settings?.frameRate === 'number' && settings.frameRate > 0) {
    return snapToCommonVideoFpsConservative(settings.frameRate)
  }
  return snapToCommonVideoFpsConservative(fallback)
}

/** Encoder ceiling for playback-driven upload — never pace faster than the capture track. */
export function resolvePlaybackDrivenEncoderFps(
  track: MediaStreamTrack,
  fallback = 24,
): number {
  return resolveTrackFrameRate(track, fallback)
}
